import 'slick-carousel/slick/slick-theme.css'
import 'slick-carousel/slick/slick.css'

import { useMediaQuery } from '@mantine/hooks'
import Image from 'next/image'
import Slider, { Settings } from 'react-slick'
import styles from './kdt-cld-3rd.module.scss'

interface IProjectRenderProps {
  contents: {
    imgUrl: string
    intro: string
    title: string
    boldPc?: string
    boldM?: string
    desc?: string
  }[]
}

const projectCustomPaging = (i: number) => {
  const dotLabels = ['레거시', 'AWS 클라우드', '하이브리드']
  return (
    <button>
      <span className="text-center">{dotLabels[i]}</span>
    </button>
  )
}

export const ProjectSliderItem = ({ contents }: IProjectRenderProps) => {
  const isMobile = useMediaQuery('(max-width: 1023px)')
  const settings = {
    dots: true,
    infinite: false,
    variableWidth: false,
    slidesToShow: 1,
    slidesToScroll: 1,
    speed: 500,
    centerMode: false,
    touchMove: true,
    arrows: true,
    adaptiveHeight: true,
    customPaging: projectCustomPaging,
  }

  return (
    <div>
      <Slider {...settings}>
        {contents.map(({ imgUrl, intro, title, boldPc, boldM, desc }) => (
          <div className="w-full overflow-hidden pt-[98px] lg:pt-[70px]" key={title}>
            <div className="flex flex-col items-center overflow-hidden lg:min-h-[288px] lg:flex-row lg:gap-20 lg:rounded lg:bg-[#1f1f1f]">
              <div className="w-full overflow-hidden rounded-t-lg lg:w-[511px] lg:rounded-none">
                <img
                  src={imgUrl}
                  className="h-full w-full object-cover"
                  loading="lazy"
                  alt={title}
                />
              </div>
              <div className="flex w-full flex-1 flex-col items-center justify-center gap-2 rounded-b-lg bg-[#313131] py-5 lg:w-auto lg:items-start lg:justify-start lg:gap-3 lg:rounded-none lg:bg-[#1f1f1f] lg:py-0">
                <p className="inline w-auto max-w-max rounded bg-[rgba(255,119,16,0.10);] px-2 py-1 text-[10px] font-semibold text-yellow-450 lg:px-3 lg:text-xs">
                  {intro}
                </p>
                <p className="whitespace-pre-wrap text-base font-bold text-white lg:text-xl">
                  {title}
                </p>
                <p className="whitespace-pre-wrap text-center text-sm leading-normal text-white lg:text-left lg:text-base">
                  {isMobile ? boldM : boldPc}
                </p>
                {desc && ( // desc가 존재할 때만 렌더링
                  <p className="-mt-1.5 whitespace-pre-wrap text-center text-sm leading-normal text-gray-400 lg:-mt-2.5 lg:text-left lg:text-base">
                    {desc}
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}
      </Slider>
    </div>
  )
}

interface ISupportCarouselProps {
  contents: {
    imgUrlM: string
    title: string
    desc: string
  }[]
}

export const SupportSliderItem = ({ contents }: ISupportCarouselProps) => {
  const settings: Settings = {
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    speed: 500,
    dots: true,
    arrows: false,
    variableWidth: true,
    touchMove: true,
    className: 'carousel',
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          centerPadding: '20px',
        },
      },
    ],
  }
  return (
    <div className={`${styles.support_slider}`}>
      <Slider {...settings}>
        {contents.map(({ desc, title, imgUrlM }) => (
          <div className="w-full" key={title}>
            <div className="pl-4 lg:pl-8">
              <div className="flex flex-col gap-[18px]">
                <div className="h-[174px] w-full overflow-hidden rounded-md">
                  <Image
                    src={imgUrlM as string}
                    layout="fill"
                    objectFit="cover"
                    className="!relative h-full w-full"
                    alt="-"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <p className="text-base font-bold text-white">{title}</p>
                  <p className="text-base leading-normal text-label-assistive">{desc}</p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </Slider>
    </div>
  )
}

interface IReviewCarouselProps {
  contents: {
    imgUrl: string
    title: string
    desc: string
  }[]
}

export const ReviewSliderItem = ({ contents }: IReviewCarouselProps) => {
  const settings: Settings = {
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    speed: 500,
    dots: true,
    arrows: false,
    variableWidth: true,
    touchMove: true,
    className: 'carousel',
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          centerPadding: '20px',
        },
      },
    ],
  }
  return (
    <div className={`${styles.review_slider}`}>
      <Slider {...settings}>
        {contents.map(({ desc, title, imgUrl }) => (
          <div className="w-full" key={title}>
            <div className="pl-4 lg:pl-8">
              <div className="flex h-[262px] flex-col gap-4 rounded-lg bg-[#303030] p-4">
                <div className="flex flex-row items-center gap-4 border-b border-[#525252] pb-4">
                  <div className="h-12 w-12">
                    <Image
                      src={imgUrl as string}
                      layout="fill"
                      objectFit="cover"
                      className="!relative h-full w-full"
                      alt="-"
                    />
                  </div>
                  <p className="whitespace-pre-wrap text-lg font-bold leading-normal text-white">
                    {title}
                  </p>
                </div>
                <p className="whitespace-pre-wrap text-base leading-normal text-label-assistive">
                  {desc}
                </p>
              </div>
            </div>
          </div>
        ))}
      </Slider>
    </div>
  )
}

interface IBenefitCarouselProps {
  contents: {
    imgUrlM: string
    title: string
    desc: string
  }[]
}

export const BenefitSliderItem = ({ contents }: IBenefitCarouselProps) => {
  const settings: Settings = {
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    speed: 500,
    dots: true,
    arrows: false,
    variableWidth: true,
    touchMove: true,
    className: 'carousel',
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          centerPadding: '20px',
        },
      },
    ],
  }
  return (
    <div className={`${styles.benefit_slider}`}>
      <Slider {...settings}>
        {contents.map(({ desc, title, imgUrlM }) => (
          <div className="w-full" key={title}>
            <div className="pl-4 lg:pl-8">
              <div>
                <img
                  src={imgUrlM}
                  className="mx-auto mb-[18px] block h-full w-full rounded object-cover"
                  loading="lazy"
                />
                <p className="mb-2 text-base font-bold text-white">{title}</p>
                <p className="whitespace-pre-wrap text-base leading-normal text-label-assistive">
                  {desc}
                </p>
              </div>
            </div>
          </div>
        ))}
      </Slider>
    </div>
  )
}
